import { supabase } from '../lib/supabase';
import { getPhasesForStatus, PHASE_STATUS_MAP } from '../constants/phaseStatusMapping';

// Valid subitem status enum values from the database
const VALID_SUBITEM_STATUS_ENUM = ['Done', 'Working on it', 'Stuck', 'N/A', null, ''];

// HELPER: Status Recognition Patterns
// Add common status values that should be considered "ready/complete"
const READY_STATUS_PATTERNS = {
  // Boolean values
  booleans: [true, 1],
  
  // String values (case-insensitive)
  strings: [
    'yes', 'y', 'true', '1',
    'complete', 'completed', 'done', 'finished',
    'ok', 'ready', 'approved', 'checked', 'verified'
  ],
  
  // Add custom patterns here based on your database investigation:
  // custom: ['your_custom_status', 'another_status']
};

// HELPER: Test if a status value indicates "ready"
const isReadyStatus = (statusValue) => {
  // Handle null/undefined/empty cases
  if (statusValue === null || statusValue === undefined || statusValue === '') {
    return { isReady: false, reason: 'null/undefined/empty value' };
  }
  
  // Check boolean and numeric values
  if (READY_STATUS_PATTERNS.booleans.includes(statusValue)) {
    return { isReady: true, reason: `boolean/number match: ${statusValue}` };
  }
  
  // Convert to string for string comparisons
  const statusStr = String(statusValue).toLowerCase().trim();
  
  // Check string patterns
  if (READY_STATUS_PATTERNS.strings.includes(statusStr)) {
    return { isReady: true, reason: `string match: "${statusStr}"` };
  }
  
  // Check custom patterns if they exist
  if (READY_STATUS_PATTERNS.custom && READY_STATUS_PATTERNS.custom.map(s => s.toLowerCase()).includes(statusStr)) {
    return { isReady: true, reason: `custom match: "${statusStr}"` };
  }
  
  return { isReady: false, reason: `no match for: ${JSON.stringify(statusValue)} (${typeof statusValue})` };
};

export class SubitemsService {
  /**
   * Fetch all subitems for a project
   * @param {number} projectId - The project ID
   * @returns {Promise<Array>} Array of subitems
   */
  static async getSubitemsByProject(projectId) {
    try {
      const { data, error } = await supabase
        .from('subitems')
        .select(`
          id,
          name,
          phase,
          subitem_status,
          user,
          employee_id,
          order,
          project_id,
          created_at,
          updated_at,
          employee:employees(
            id,
            name,
            email
          )
        `)
        .eq('project_id', projectId)
        .order('order', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch subitems: ${error.message}`);
      }

      // Transform snake_case to camelCase
      return (data || []).map(item => ({
        id: item.id,
        name: item.name,
        phase: item.phase,
        subitemStatus: item.subitem_status,
        user: item.user, // Keep for backward compatibility
        employeeId: item.employee_id,
        employee: item.employee,
        order: item.order,
        projectId: item.project_id,
        createdAt: item.created_at,
        updatedAt: item.updated_at
      }));
    } catch (error) {
      console.error('Error fetching subitems:', error);
      throw error;
    }
  }

  /**
   * Fetch subitems filtered by modeling status
   * @param {number} projectId - The project ID
   * @param {string} modelingStatus - The current modeling status to filter by
   * @returns {Promise<Array>} Array of filtered subitems
   */
  static async getSubitemsByStatus(projectId, modelingStatus) {
    try {
      // First, let's get ALL subitems for this project to see what we have
      const { data: allSubitems } = await supabase
        .from('subitems')
        .select(`
          id,
          name,
          phase,
          subitem_status,
          user,
          employee_id,
          order,
          project_id,
          created_at,
          updated_at
        `)
        .eq('project_id', projectId)
        .order('phase', { ascending: true })
        .order('order', { ascending: true });
        
      console.log('🔍 DEBUG: Raw SQL Query (conceptual): SELECT * FROM subitems WHERE project_id =', projectId);
      console.log('🔍 DEBUG: Raw database response - All phases found:', [...new Set((allSubitems || []).map(item => item.phase))].sort());
      
      if (allSubitems && allSubitems.length > 0) {
        allSubitems.slice(0, 3).forEach(item => {
        });
        
        const reviewItems = allSubitems.filter(item => item.phase === '08-Review');
        console.log('🔍 DEBUG: Raw Review items (phase = "08-Review"):', reviewItems.length);
        reviewItems.forEach(item => {
        });
        
        // Check if any Review items have N/A status (this could be why they're filtered out)
        const reviewWithNA = reviewItems.filter(item => item.subitem_status === 'N/A');
        if (reviewWithNA.length > 0) {
          console.log('⚠️ DEBUG: WARNING - Found Review items with N/A status (these will be filtered out by .neq filter):');
          reviewWithNA.forEach(item => {
          });
        }
      }

      // For Review status, return ALL subitems (same as other statuses)
      // SubitemsTable handles the filtering/grouping into sections
      // Previously this had special-case logic that broke "All Project Subitems" display

      // For Ready to Export status, also return ALL subitems
      // The SubitemsTable component will handle filtering
      if (modelingStatus === 'Ready to Export') {
        const { data, error } = await supabase
          .from('subitems')
          .select(`
            id,
            name,
            phase,
            subitem_status,
            user,
            employee_id,
            order,
            project_id,
            created_at,
            updated_at,
            employee:employees(
              id,
              name,
              email
            )
          `)
          .eq('project_id', projectId)
          .order('phase', { ascending: true })
          .order('order', { ascending: true });

        if (error) {
          throw new Error(`Failed to fetch filtered subitems: ${error.message}`);
        }
        console.log(`🔍 DEBUG: All phases in dataset:`, [...new Set((data || []).map(item => item.phase))].sort());

        // Transform and return the data
        return (data || []).map(item => ({
          id: item.id,
          name: item.name,
          phase: item.phase,
          subitemStatus: item.subitem_status,
          user: item.user,
          employeeId: item.employee_id,
          employee: item.employee,
          order: item.order,
          projectId: item.project_id,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        }));
      }

      // For ALL other statuses, return ALL subitems (not just current phase)
      // The SubitemsTable component will handle filtering for incomplete previous phase items
      const { data, error } = await supabase
        .from('subitems')
        .select(`
          id,
          name,
          phase,
          subitem_status,
          user,
          employee_id,
          order,
          project_id,
          created_at,
          updated_at,
          employee:employees(
            id,
            name,
            email
          )
        `)
        .eq('project_id', projectId)
        .order('phase', { ascending: true })
        .order('order', { ascending: true });

      if (error) {
        throw new Error(`Failed to fetch filtered subitems: ${error.message}`);
      }
      console.log(`🔍 DEBUG: All phases in dataset:`, [...new Set((data || []).map(item => item.phase))].sort());
      
      // Transform snake_case to camelCase
      return (data || []).map(item => ({
        id: item.id,
        name: item.name,
        phase: item.phase,
        subitemStatus: item.subitem_status,
        user: item.user, // Keep for backward compatibility
        employeeId: item.employee_id,
        employee: item.employee,
        order: item.order,
        projectId: item.project_id,
        createdAt: item.created_at,
        updatedAt: item.updated_at
      }));
    } catch (error) {
      console.error('Error fetching filtered subitems:', error);
      throw error;
    }
  }

  /**
   * Update a subitem
   * @param {number} subitemId - The subitem ID
   * @param {object} updates - The fields to update
   * @returns {Promise<object>} Updated subitem
   */
  static async updateSubitem(subitemId, updates) {
    try {
      const dbUpdates = {};
      
      // Map camelCase to snake_case for database
      if (updates.name !== undefined) dbUpdates.name = updates.name;
      if (updates.phase !== undefined) dbUpdates.phase = updates.phase;
      if (updates.subitemStatus !== undefined) {
        // Validate and map status values to ensure they match the database enum
        // Valid enum values: "Done", "Working on it", "Stuck", "N/A", null
        let validStatus = updates.subitemStatus;

        // Map any variations of "Completed" to "Done"
        if (validStatus && typeof validStatus === 'string') {
          // Remove any hidden characters and normalize
          validStatus = validStatus.trim().replace(/[\u200B-\u200D\uFEFF]/g, '');
          const statusLower = validStatus.toLowerCase();

          console.log('🔍 Status validation:', {
            original: updates.subitemStatus,
            cleaned: validStatus,
            lowercase: statusLower,
            charCodes: Array.from(validStatus).map(c => `${c}:${c.charCodeAt(0)}`)
          });

          if (statusLower === 'completed' || statusLower === 'complete' || statusLower.includes('complet')) {
            console.warn(`⚠️ Mapping invalid status "${validStatus}" to "Done"`);
            validStatus = 'Done';
          }

          // Also check if it somehow contains "Done" but with extra characters
          if (validStatus !== 'Done' && validStatus !== 'Working on it' && validStatus !== 'Stuck' && validStatus !== 'N/A') {
            console.warn(`⚠️ Unknown status "${validStatus}" - forcing to "Done" if it was meant to be a completion`);
            if (statusLower.includes('done') || statusLower === 'yes' || statusLower === 'true') {
              validStatus = 'Done';
            }
          }
        }

        // Ensure the status is one of the valid enum values
        if (!VALID_SUBITEM_STATUS_ENUM.includes(validStatus)) {
          console.error(`❌ Invalid subitem status: "${validStatus}". Using null instead.`);
          validStatus = null;
        }

        dbUpdates.subitem_status = validStatus || null;
      }
      if (updates.user !== undefined) dbUpdates.user = updates.user;
      if (updates.employeeId !== undefined) dbUpdates.employee_id = updates.employeeId;
      if (updates.order !== undefined) dbUpdates.order = updates.order;

      // DEBUG: Log exactly what we're sending to the database
      console.log('🔍 CRITICAL DEBUG - About to send to Supabase:', {
        subitemId,
        dbUpdates,
        subitem_status_value: dbUpdates.subitem_status,
        status_type: typeof dbUpdates.subitem_status,
        status_charCodes: dbUpdates.subitem_status ? Array.from(String(dbUpdates.subitem_status)).map(c => c.charCodeAt(0)) : null,
        allKeys: Object.keys(dbUpdates),
        fullObject: JSON.stringify(dbUpdates)
      });
      
      // EXTRA DEBUG: Let's see if Done is really Done
      if (dbUpdates.subitem_status === 'Done') {
      } else if (dbUpdates.subitem_status) {
      }

      const { data, error } = await supabase
        .from('subitems')
        .update(dbUpdates)
        .eq('id', subitemId)
        .select(`
          id,
          name,
          phase,
          subitem_status,
          user,
          employee_id,
          order,
          project_id,
          created_at,
          updated_at,
          employee:employees(
            id,
            name,
            email
          )
        `)
        .single();

      if (error) {
        // Special handling for enum error
        if (error.message && error.message.includes('invalid input value for enum subitem_status_enum')) {
          console.error('🔴 Enum error detected. Attempting retry with "Done" status...');
          
          // If we get the Completed enum error, retry with Done
          if (error.message.includes('"Completed"')) {
            console.log('🔴 Current dbUpdates causing error:', JSON.stringify(dbUpdates));
            
            // Create a clean update object with only the status
            const cleanUpdate = {
              subitem_status: 'Done'
            };
            
            console.log('🔄 Retrying with clean update:', JSON.stringify(cleanUpdate));
            
            const { data: retryData, error: retryError } = await supabase
              .from('subitems')
              .update(cleanUpdate)
              .eq('id', subitemId)
              .select(`
                id,
                name,
                phase,
                subitem_status,
                user,
                employee_id,
                order,
                project_id,
                created_at,
                updated_at,
                employee:employees(
                  id,
                  name,
                  email
                )
              `)
              .single();
            
            if (retryError) {
              throw new Error(`Failed to update subitem (retry failed): ${retryError.message}`);
            }
            // Transform and return the retry data
            return {
              id: retryData.id,
              name: retryData.name,
              phase: retryData.phase,
              subitemStatus: retryData.subitem_status,
              user: retryData.user,
              employeeId: retryData.employee_id,
              employee: retryData.employee,
              order: retryData.order,
              projectId: retryData.project_id,
              createdAt: retryData.created_at,
              updatedAt: retryData.updated_at
            };
          }
        }
        
        throw new Error(`Failed to update subitem: ${error.message}`);
      }

      // Transform response back to camelCase
      return {
        id: data.id,
        name: data.name,
        phase: data.phase,
        subitemStatus: data.subitem_status,
        user: data.user,
        employeeId: data.employee_id,
        employee: data.employee,
        order: data.order,
        projectId: data.project_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (error) {
      console.error('Error updating subitem:', error);
      throw error;
    }
  }
  
  /**
   * Get employee by email
   * @param {string} email - The employee email
   * @returns {Promise<object|null>} Employee object or null
   */
  static async getEmployeeByEmail(email) {
    try {
      const { data, error } = await supabase
        .from('employees')
        .select('id, name, email')
        .eq('email', email)
        .single();

      if (error) {
        if (error.code === 'PGRST116') {
          // No matching employee found
          return null;
        }
        throw error;
      }

      return data;
    } catch (error) {
      console.error('Error fetching employee by email:', error);
      throw error;
    }
  }

  /**
   * Create a new subitem
   * @param {object} subitemData - The subitem data
   * @returns {Promise<object>} Created subitem
   */
  static async createSubitem(subitemData) {
    try {
      // Validate and map status values to ensure they match the database enum
      let validStatus = subitemData.subitemStatus;
      if (validStatus && typeof validStatus === 'string') {
        const statusLower = validStatus.toLowerCase().trim();
        if (statusLower === 'completed' || statusLower === 'complete') {
          console.warn(`⚠️ Mapping invalid status "${validStatus}" to "Done" during creation`);
          validStatus = 'Done';
        }
      }
      
      // Ensure the status is one of the valid enum values
      if (validStatus && !VALID_SUBITEM_STATUS_ENUM.includes(validStatus)) {
        console.error(`❌ Invalid subitem status during creation: "${validStatus}". Using null instead.`);
        validStatus = null;
      }
      
      const { data, error } = await supabase
        .from('subitems')
        .insert([{
          name: subitemData.name,
          phase: subitemData.phase,
          subitem_status: validStatus || null,
          user: subitemData.user,
          order: subitemData.order,
          project_id: subitemData.projectId
        }])
        .select()
        .single();

      if (error) {
        throw new Error(`Failed to create subitem: ${error.message}`);
      }

      // Transform response to camelCase
      return {
        id: data.id,
        name: data.name,
        phase: data.phase,
        subitemStatus: data.subitem_status,
        user: data.user,
        order: data.order,
        projectId: data.project_id,
        createdAt: data.created_at,
        updatedAt: data.updated_at
      };
    } catch (error) {
      console.error('Error creating subitem:', error);
      throw error;
    }
  }

  /**
   * Delete a subitem
   * @param {number} subitemId - The subitem ID
   * @returns {Promise<boolean>} Success status
   */
  static async deleteSubitem(subitemId) {
    try {
      const { error } = await supabase
        .from('subitems')
        .delete()
        .eq('id', subitemId);

      if (error) {
        throw new Error(`Failed to delete subitem: ${error.message}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting subitem:', error);
      throw error;
    }
  }
}